<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">
<!--        {{ !form.id ? $t('userAccessCreateModalTitle') : $t('userAccessEditModalTitle') }}-->
        {{ $t('userRoleAssignmentCreateModalTitle') }}
      </div>
    </template>

    <Spinner v-if="dataIsSending" :isSmall="true" :spinnerStyle="'height:122px'"/>

    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <CustomDropdown id="user_assignment_location"
                          v-model="selectedLocation"
                          :options="locationOptions"
                          :clearSearchData="!visible"
                          optionLabel="public_name"
                          data-key="id"
                          :filter="true"
                          :showClear="false"
                          :class="{'p-invalid': submitted && !selectedLocation}">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{ slotProps.value.public_name }}</span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{ slotProps.option.public_name }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="user_assignment_location">
            {{ $t('Location') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedLocation">{{ $t('Required field') }}</small>
      </div>

      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12">
        <div class="p-float-label">
          <CustomDropdown id="user_assignment_role"
                          v-model="selectedRole"
                          :options="roleOptions"
                          :clearSearchData="!visible"
                          optionLabel="name"
                          data-key="id"
                          :filter="true"
                          :showClear="false"
                          :disabled="!selectedLocation"
                          :class="{'p-invalid': submitted && !selectedRole}">
            <template #value="slotProps">
              <div v-if="slotProps.value">
                <span>{{ slotProps.value.name }}</span>
              </div>
              <span v-else style="visibility: hidden">.</span>
            </template>
            <template #option="slotProps">
              <div>
                <span>{{ slotProps.option.name }}</span>
              </div>
            </template>
          </CustomDropdown>
          <label for="user_assignment_role">
            {{ $t('Role') }}<span class="warning-color">*</span>
          </label>
        </div>
        <small class="p-invalid" v-if="submitted && !selectedRole">{{ $t('Required field') }}</small>
      </div>
    </div>

    <template #footer>
      <Button :label="$t('Cancel')"
              icon="pi pi-times"
              class="p-button-text"
              @click="close"/>
      <Button :label="$t('Save')"
              :loading="disableSaveButton || dataIsSending"
              :disabled="disableSaveButton || dataIsSending || !roleOptions.length"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import roles from '@/translations/roles'
import showErrorsMixins from "@/mixins/showErrorsMixins";
import constants from "@/constants";
import settings from "@/settings";

export default {
  name: 'UserAccessModal',
  mixins: [showErrorsMixins],
  emits: ['close', 'update-items'],
  props: {
    item: {
      type: Object,
      default: null,
    },
    visible: Boolean,
    userId: [Number, String],
    locations: {
      type: Array,
      default: () => []
    },
    accessMap: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      submitted: false,
      disableSaveButton: false,
      dataIsSending: false,
      form: {
        id: null,
        user_id: null,
        location_id: null,
        role: null,
        status: 1,
      },
      selectedLocation: null,
      selectedRole: null,
    }
  },
  watch: {
    item(value) {
      if (!value) {
        this.resetForm()
        return
      }

      this.form = {
        id: value.id ?? null,
        user_id: value.user_id ?? this.userId ?? null,
        location_id: value.location_id ?? null,
        role: value.role ?? null,
        status: value.status ?? 1,
      }

      if (value.id) {
        this.selectedLocation = this.findLocationById(value.location_id)
      } else if (this.locations.length === 1) {
        this.selectedLocation = this.locations[0]
      }

      // this.selectedLocation = this.findLocationById(value.location_id)
      this.selectedRole = this.findRoleById(value.role)

      this.submitted = false
      this.disableSaveButton = false
      this.dataIsSending = false

      this.$nextTick(() => {
        this.normalizeSelectedRole()
      })
    },

    visible(value) {
      if (!value) {
        this.submitted = false
        this.disableSaveButton = false
        this.dataIsSending = false

        this.selectedLocation= null
        this.selectedRole= null
      }
    },

    selectedLocation() {
      this.normalizeSelectedRole()
    }
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return { position: 'absolute', top: 0, width: '95%', maxWidth: '380px', marginTop: '10px' }
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return { position: 'absolute', top: 0, width: '380px', marginTop: '25px' }
      } else {
        return { position: 'absolute', top: 0, width: '380px', marginTop: '50px' }
      }
    },

    locationOptions() {
      return (this.locations || []).map(item => ({
        id: +item.id,
        public_name: item.public_name || ''
      }))
    },

    roleOptions() {
      if (!this.selectedLocation?.id) {
        return []
      }

      const superAdmin = constants.userRoles.superAdmin
      const owner = constants.userRoles.owner
      const admin = constants.userRoles.admin
      const accountant = constants.userRoles.accountant
      const customer = constants.userRoles.customer
      const employeeWorkshopMaster = constants.userRoles.employeeWorkshopMaster

      const locationAccess = Array.isArray(this.accessMap)
          ? this.accessMap.find(item => +item.location_id === +this.selectedLocation.id)
          : null


      const existingRoles = Array.isArray(locationAccess?.roles)
          ? locationAccess.roles.map(role => +role)
          : []

      console.log(locationAccess)

      return roles
          .filter(role => {
            const roleNum = +role.num

            if (
                roleNum === superAdmin ||
                roleNum === customer ||
                roleNum === employeeWorkshopMaster
            ) {
              return false
            }

            if (this.form.id && +this.form.role === roleNum) {
              return true
            }

            if (this.$store.getters.isOwnerOrHigher) {
              // owner может всё кроме superAdmin/customer/master
            } else if (this.$store.getters.isAdmin || this.$store.getters.isAccountant) {
              return roleNum !== owner && roleNum !== admin &&  roleNum !== accountant
            } else {
              return false
            }

            return !existingRoles.includes(roleNum)
          })
          .map(role => ({
            id: +role.num,
            name: role.name?.[this.$i18n.locale] || role.name?.en || String(role.num)
          }))
    }
  },
  methods: {
    resetForm() {
      this.form = {
        id: null,
        user_id: this.userId ?? null,
        location_id: null,
        role: null,
        status: 1,
      }
      this.selectedLocation = null
      this.selectedRole = null
      this.submitted = false
      this.disableSaveButton = false
      this.dataIsSending = false
    },

    findLocationById(locationId) {
      return this.locationOptions.find(item => +item.id === +locationId) || null
    },

    findRoleById(roleId) {
      if (!roleId) return null

      const roleData = roles.find(role => +role.num === +roleId)
      if (!roleData) return null

      return {
        id: +roleData.num,
        name: roleData.name?.[this.$i18n.locale] || roleData.name?.en || String(roleData.num)
      }
    },

    normalizeSelectedRole() {
      if (!this.selectedLocation?.id) {
        this.selectedRole = null
        return
      }

      const roleExists = this.roleOptions.some(item => +item.id === +this.selectedRole?.id)

      if (!roleExists) {
        this.selectedRole = null
      }
    },

    async saveItem() {
      this.submitted = true

      if (!this.selectedLocation?.id) return
      if (!this.selectedRole?.id) return

      this.disableSaveButton = true
      this.dataIsSending = true

      const payload = {
        assignment: {
          id: this.form.id ?? null,
          user_id: this.form.user_id ?? this.userId ?? null,
          location_id: this.selectedLocation?.id || null,
          role: this.selectedRole?.id || null,
          status: this.form.status ?? 1,
        }
      }

      try {
        const isEdit = !!this.form.id
        const url = isEdit ? 'user-access/update-user-assignment' : 'user-access/create-user-assignment'
        const expectedStatus = isEdit ? 200 : 201

        const { status, data } = await httpClient.post(url, payload)

        if (status === expectedStatus && data?.success) {
          const id = this.form.id ?? data.id
          this.$emit('update-items', id)

          this.$toast.add({
            severity:'success',
            detail: isEdit ? this.$t('Data updated') : this.$t('Data saved'),
            life: settings.toastLife
          });


          if (data.access_map) this.$store.commit('setAccessMap', data.access_map)

          this.close()
          return
        }

        if (data?.error?.detail) {
          this.showError(data.error.detail)
          return
        }

        this.showUnknownError()
      } catch (err) {
        this.showError(err)
      } finally {
        this.dataIsSending = false
        this.disableSaveButton = false
      }
    },

    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
}
</script>

<style scoped lang="scss">
.p-button-text {
  width: auto;
}
</style>